import { getBaseCurrency } from "@/constant/currency"
import { liveLobbyOf } from "@/constant/liveLobby"
import * as S from './styled'

import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCommonState } from '@/core/context/useContext'
import { usePopup } from '@/core/context/useWindow'
import { t } from 'i18next'

import Login from '@/components/popup/Login'

import api from '@/core/services/api'
import useFormData from '@/core/hooks/useFormData'
import useTableData from '@/core/hooks/useTableData'
import { setMenuData, resetMenuData } from '@/core/store/menu/actions'
import { assetThumb } from '@/core/services/thumbUrl'

const CATEGORY_CONFIG = {
  '01_02': { labelKey: 'gameList.slot', category: 'slot' },
  '01_01': { labelKey: 'gold.live', category: 'live' },
}

// 카지노(LIVE) 벳지 클릭 시 로비 직접 이동 — 일본(그랜드라인) 빌드에서만 적용
const LIVE_LOBBY_ENABLED = process.env.REACT_APP_LIVE_LOBBY === 'true'

/**
 * 벤더 게임 목록 컴포넌트
 * 01_01(카지노) + 01_02(슬롯) 벤더를 합쳐서 표시
 * 호버 시 해당 벤더가 속한 카테고리의 버튼만 노출
 */
export default function VendorList() {
  const { openPopup } = usePopup()
  const dispatch = useDispatch()
  const { isLogin } = useCommonState()
  const menuData = useSelector((state) => state.Menu)

  // side_menu 최상위가 [01_02, 01_01] 형태 또는 [01 > subMenus] 형태 모두 대응
  const findNode = (code) =>
    menuData?.find?.(m => m.menuCode === code) ||
    menuData?.[0]?.subMenus?.find?.(m => m.menuCode === code)
  const slotVendors = findNode('01_02')?.subMenus || []
  const liveVendors = findNode('01_01')?.subMenus || []

  // 벤더 name 기준으로 합치기: { name, thumbnail, categories: [{ parentCode, menuCode }] }
  // 순서는 무조건 슬롯(01_02_X) sort 기준 — 슬롯에 게임이 없어도(hasGame=0) 노드는 받으므로 순서 유지.
  // 벳지(categories)는 hasGame=1 인 카테고리만 표시, 둘 다 0이면 카드 제외.
  const mergedVendors = useMemo(() => {
    const map = new Map()

    slotVendors.forEach(v => {
      if (!map.has(v.name)) {
        map.set(v.name, { name: v.name, thumbnail: v.thumbnail, sort: v.sort, categories: [] })
      }
      const entry = map.get(v.name)
      if (!entry.thumbnail) entry.thumbnail = v.thumbnail
      // hasGame 이 응답에 없으면(구버전 백엔드) undefined → true 로 폴백해서 노출.
      // 백엔드가 hasGame(1/0)을 주면 그대로 존중(0이면 제외).
      if (v.hasGame ?? true) entry.categories.push({ parentCode: '01_02', menuCode: v.menuCode })
    })

    liveVendors.forEach(v => {
      // 슬롯 노드가 없을 때만 라이브 sort(+1000) 기준으로 신규 등록
      if (!map.has(v.name)) {
        map.set(v.name, { name: v.name, thumbnail: v.thumbnail, sort: v.sort + 1000, categories: [] })
      }
      const entry = map.get(v.name)
      if (!entry.thumbnail) entry.thumbnail = v.thumbnail
      if (v.hasGame ?? true) entry.categories.push({ parentCode: '01_01', menuCode: v.menuCode })
    })

    return Array.from(map.values())
      .filter(entry => entry.categories.length > 0)
      .sort((a, b) => a.sort - b.sort)
  }, [slotVendors, liveVendors])

  const [isLoading, setIsLoading] = useState(true)

  // gold 레이아웃에는 카테고리 필터 탭이 없다 — 전체 벤더를 그대로 보여준다.
  const visibleVendors = mergedVendors

  const handleGetData = () => {
    api.get(`/game/side-menu`)
    .then((res) => {
      const data = res?.data?.content?.side_menu || []
      dispatch(setMenuData(data))
    })
    .catch((err) => {
      console.error('Error fetching menu:', err)
      dispatch(resetMenuData())
    })
    .finally(() => setIsLoading(false))
  }

  const getData = useTableData(true)
  useFormData({}, handleGetData, getData)

  // gold8.bet 처럼 게임사 게임 목록은 새 창(탭)으로 연다 — /lobby/:menuCode (pages/Lobby)
  const openGameList = (menuCode, category) => {
    window.open(`/lobby/${menuCode}?category=${category}`, '_blank')
  }

  // 라이브 단일 벤더 → 게임사 로비 직접 이동
  const handleLiveOnlyClick = (vendor) => {
    if (!isLogin) {
      openPopup({ grandLogin: true, title: t('modalTitle.login'), content: <Login /> })
      return
    }
    // 일본(그랜드라인) 빌드에서만 로비 직접 이동
    if (LIVE_LOBBY_ENABLED) {
      const entry = liveLobbyOf(vendor.name)
      if (entry) {
        // 라이브 로비도 게임 목록과 같이 새 창으로 연다
        window.open(`/game-play/${entry.vendor}/${entry.id}/${getBaseCurrency()}`, '_blank')
        return
      }
    }
    // 로비 미적용/매핑 없으면 일반 게임 리스트 팝업 fallback
    const liveCat = vendor.categories.find(c => c.parentCode === '01_01')
    if (liveCat) openGameList(liveCat.menuCode, 'live')
  }

  // 메뉴 데이터 로딩 중 → 빈 타일
  if (isLoading || !menuData || menuData.length === 0) {
    return (
      <S.Grid>
        {Array.from({ length: 12 }).map((_, i) => <S.Tile key={i}><S.TileBg /></S.Tile>)}
      </S.Grid>
    )
  }
  if (!visibleVendors.length) return null

  // gold8.bet: 타일에 마우스를 올리면 어두운 막 위에 입구 버튼(スロット/ライブ)이 세로로 나온다.
  return (
    <S.Grid>
      {visibleVendors.map((vendor) => (
        <S.Tile key={vendor.name}>
          <S.TileBg>
            {vendor.thumbnail && (
              <img
                src={assetThumb(vendor.thumbnail, 700)}
                alt={vendor.name}
                loading="lazy"
                decoding="async"
                onError={(e) => { e.target.style.display = 'none' }}
              />
            )}
          </S.TileBg>
          <S.Entrance>
            {vendor.categories.map(({ parentCode, menuCode }) => {
              const cfg = CATEGORY_CONFIG[parentCode]
              if (!cfg) return null
              const onClick = (e) => {
                e.stopPropagation()
                if (parentCode === '01_01') { handleLiveOnlyClick(vendor); return }
                openGameList(menuCode, cfg.category)
              }
              return <S.EntranceBtn key={parentCode} type="button" onClick={onClick}>{t(cfg.labelKey)}</S.EntranceBtn>
            })}
          </S.Entrance>
        </S.Tile>
      ))}
    </S.Grid>
  )
}
